<template>
<div class="key-actions-section" @click.stop='goToIssues'>
    <span class="key-action-label">{{$t('dashboard.actions.table.label')}}</span>
    <div class="key-actions-table">
      <div class="key-actions-table-content" :class='{ loading: dashboardIssuesListLoading }'>
        <wombat-table
          :items="formatItems.slice(0,5)"
          :columns="tableColumns"
          :loadingItems="false"
          :resize="true"
          :columnsReorder="false"
          :noResultsMessage="$t('noResultsFound')"
        >
          <component
            :costType="rowCell.column.name"
            slot="row-cell"
            slot-scope="rowCell"
            class="row-cell"
            :is="tableRowCellComponentsHash[rowCell.column.name] || tableRowCellComponentsHash.default"
            :item="rowCell.item"
            :column="rowCell.column"
          />
        </wombat-table>
      </div>
      <div class='key-actions-table-loading' v-if='dashboardIssuesListLoading'>
        <loading-spinner :height='"150"'/>
      </div>
    </div>
</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { advisedActionsColumns } from '@/services/tableColumns';
import WombatTable from '@/components/WombatTable/WombatTable.vue';
import InitiateActionTableCell from '@/components/tableCells/neededActionsTableCells/InitiateActionTableCell.vue';
import WombatRowDefaultCell from '@/components/WombatTable/WombatRowDefaultCell.vue';
import AdvisedActionNameTableCell from '@/components/tableCells/neededActionsTableCells/AdvisedActionNameTableCell.vue';
import ImpactScoreTableCell from '@/components/tableCells/neededActionsTableCells/ImpactScoreTableCell.vue';
import SeverityTableCell from '@/components/tableCells/neededActionsTableCells/SeverityTableCell.vue';
import AffectedAssetTableCell from '@/components/tableCells/neededActionsTableCells/AffectedAssetTableCell.vue';
import { views } from '@/router/constants';
import IssueCycleTableCell from '@/components/tableCells/centralizedIssuesTableCells/IssueCycleTableCell.vue';
import DateTableCell from '@/components/tableCells/centralizedIssuesTableCells/DateTableCell.vue';
import LoadingSpinner from '@/components/common/LoadingSpinner.vue';
import IssueEPSSTableCell from '@/components/tableCells/centralizedIssuesTableCells/IssueEPSSTableCell.vue';

export default {
  name: 'DashboardActionsToTake',
  components: {
    WombatTable,
    InitiateActionTableCell,
    AdvisedActionNameTableCell,
    ImpactScoreTableCell,
    SeverityTableCell,
    LoadingSpinner,
  },
  methods: {
    async goToIssues() {
      await this.$router.push({ name: views.ISSUES});
    },
  },
  computed: {
    ...mapGetters(['userId', 'profile', 'domains', 'advisedActions', 'centralizedIssuesForDashboard', 'dashboardIssuesListLoading']),
    tableColumns() {
      return [
        advisedActionsColumns.actionId,
        advisedActionsColumns.advisedAction,
        advisedActionsColumns.domainAndAsset,
        advisedActionsColumns.impactScore,
        advisedActionsColumns.likelihoodAttack,
        advisedActionsColumns.firstSeen,
        advisedActionsColumns.lastSeen,
        advisedActionsColumns.cycles,
        // advisedActionsColumns.estimatedBreachCost,
        // advisedActionsColumns.estimatedFixCost,
        // bugfix/CYN-1789-multiple-ui-currections, remove configuration icon from row
        // advisedActionsColumns.takeAction,
      ].map(column => ({
        ...column,
        title: column.title && this.$t(column.title),
      }));
    },
    formatItems() {
      return this.centralizedIssuesForDashboard.map((issue, index) => ({
        id: index + 1,
        objectId: issue.id,
        advisedAction: issue.name,
        displayName: issue.displayName,
        domainAndAsset: { domain: issue.tld, asset: issue.asset },
        impactScore: issue.severity,
        likelihoodAttack: issue.probability,
        takeAction: true,
        cycles: issue.issueCount,
        firstSeen: issue.firstDate,
        lastSeen: issue.lastDate,
        description: issue.name === 'Exposed Ports and Services' ? issue.displayName : issue.metaData.description || 'No description',
        metaData: issue.metaData,
      }));
    },
    tableRowCellComponentsHash() {
      return {
        // takeAction: InitiateActionTableCell,
        advisedAction: AdvisedActionNameTableCell,
        domainAndAsset: AffectedAssetTableCell,
        impactScore: SeverityTableCell,
        likelihoodAttack: IssueEPSSTableCell,
        cycles: IssueCycleTableCell,
        firstSeen: DateTableCell,
        lastSeen: DateTableCell,
        // estimatedBreachCost: EstimatedCostTableCell,
        // estimatedFixCost: EstimatedCostTableCell,
        default: WombatRowDefaultCell,
      };
    },
  },
};
</script>

<style lang="scss">
@import '~@/assets/styles/design-system';

.key-actions-section{
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-width: 0;
   .wombat-row-default-cell {
    color: var(--text-primary);
  }
  &:hover {
    cursor: pointer;
  }
}
.key-action-label{
  color: var(--text-primary);
  font-family: $font-family-bold;
  font-size: rem(16);
  font-weight: bold;
  line-height: rem(20);
  letter-spacing: 0;
  margin-bottom: rem(10);
}

.key-actions-table{
  box-sizing: border-box;
  bottom: 1px;
  border: 1px solid var(--border-primary);
  border-radius: $radius-md;
  background-color: var(--bg-secondary);
  flex-grow: 1;
  overflow-x: hidden;
  min-width: 0;
  .key-actions-table-content {
    .wombat-table {
      --displayactioncell:none;
      padding: rem(15) 0;
      .wombat-overlay {
        padding: 0 rem(20);
        border-bottom: solid 1px var(--border-secondary) !important;
        &:hover{
          // Use Sidebar Active BG which is handled in themes
          background-color: var(--sidebar-active-bg);
          color: red;
          --display:block;
          // bugfix/CYN-1789-multiple-ui-currections, hover effect hiding cycles data
          //--bg: linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(244,244,248,0) 78%, rgba(230,230,239,0.97) 91%, #E6E6EF 100%);
          --displayactioncell: flex;
        }
      }
      .wombat-header {
        padding: 0 rem(10);
      }
    }
    &.loading {
      display: none;
    }
  }
  .key-actions-table-loading {
    align-self: center;
    justify-self: center;
    margin-bottom: 3% !important;
  }
}
</style>
<style lang='scss'>
@import '~@/assets/styles/design-system';
.key-actions-table-loading {
  .loading-spinner {
    margin-top: 0px !important;
  }
}
.key-actions-table .wombat-table .wombat-row {
  border: 0 !important;
}
.key-actions-table {
  .wombat-table {
    padding: rem(15) 0 0 0 !important;
  }
}
.key-actions-table .wombat-table .wombat-header-default-cell {
  font-family: $font-family-regular;
  font-size: rem(12);
  letter-spacing: rem(1.1);
  line-height: rem(15);
  color: var(--text-secondary);
}
.wombat-table .cell.column-takeaction{
  min-width: 0 !important;
}
.wombat-table .cell.column-likelihoodattack{
  .issue-epss-table-cell{
    justify-content: center;
  }
}
.cell.column-domainandasset, .cell.column-impactscore, .cell.column-likelihoodattack, .cell.column-firstseen, .cell.column-lastseen, .cell.column-cycles {
  .content {
    flex: none !important;
  }
}
</style>
